import React, { useState } from 'react'
import { 
  Check, 
  Crown, 
  Star, 
  Zap, 
  Heart,
  DollarSign,
  Users,
  Lock,
  Gift,
  TrendingUp,
  Gem,
  Coins,
  Shield,
  Globe,
  Rocket,
  Award
} from 'lucide-react'

interface Web3SubscriptionPlan {
  id: string
  name: string
  price: number
  currency: string
  interval: 'month' | 'year'
  description: string
  features: string[]
  web3Features: string[]
  popular?: boolean
  icon: React.ReactNode
  color: string
  badge?: string
  nftRewards?: boolean
  tokenRewards?: number
  daoAccess?: boolean
  defiBenefits?: boolean
}

interface Web3SubscriptionPlansProps {
  creatorAddress: string
  onSubscribe: (planId: string, amount: number) => void
  currentPlan?: string
  isLoading?: boolean
}

const web3Plans: Web3SubscriptionPlan[] = [
  {
    id: 'fan',
    name: 'Fan',
    price: 3,
    currency: 'SUI',
    interval: 'month',
    description: 'Support your favorite creator with basic access',
    features: [
      'Access to fan-only content',
      'Community chat access',
      'Creator updates',
      'Basic support'
    ],
    web3Features: [
      'Fan token airdrops',
      'Basic NFT access',
      'Community governance voting'
    ],
    icon: <Heart className="w-6 h-6" />,
    color: 'from-pink-500 to-rose-500',
    tokenRewards: 10
  },
  {
    id: 'supporter',
    name: 'Supporter',
    price: 8,
    currency: 'SUI',
    interval: 'month',
    description: 'Enhanced access with premium features',
    features: [
      'All fan benefits',
      'Early access to content',
      'Exclusive community channels',
      'Priority support',
      'Monthly creator calls'
    ],
    web3Features: [
      'Premium token rewards',
      'Exclusive NFT drops',
      'Advanced governance rights',
      'DeFi yield sharing'
    ],
    popular: true,
    icon: <Star className="w-6 h-6" />,
    color: 'from-purple-500 to-violet-500',
    badge: 'Most Popular',
    nftRewards: true,
    tokenRewards: 50,
    defiBenefits: true
  },
  {
    id: 'vip',
    name: 'VIP',
    price: 25,
    currency: 'SUI',
    interval: 'month',
    description: 'Ultimate creator experience with maximum benefits',
    features: [
      'All supporter benefits',
      '1-on-1 creator sessions',
      'Custom content requests',
      'Exclusive merchandise',
      'VIP community access',
      'Revenue sharing'
    ],
    web3Features: [
      'Premium token allocation',
      'Rare NFT collections',
      'DAO governance power',
      'Advanced DeFi strategies',
      'Cross-chain benefits',
      'Early access to new features'
    ],
    icon: <Crown className="w-6 h-6" />,
    color: 'from-gold-500 to-yellow-500',
    badge: 'Ultimate',
    nftRewards: true,
    tokenRewards: 200,
    daoAccess: true,
    defiBenefits: true
  },
  {
    id: 'dao-member',
    name: 'DAO Member',
    price: 100,
    currency: 'SUI',
    interval: 'month',
    description: 'Full governance and ecosystem participation',
    features: [
      'All VIP benefits',
      'Direct creator collaboration',
      'Business partnership opportunities',
      'Revenue sharing agreements',
      'Co-creation projects',
      'Exclusive events'
    ],
    web3Features: [
      'Governance token allocation',
      'Rare NFT minting rights',
      'DAO proposal creation',
      'Treasury management access',
      'Cross-platform integration',
      'Protocol development input'
    ],
    icon: <Shield className="w-6 h-6" />,
    color: 'from-blue-500 to-cyan-500',
    badge: 'Governance',
    nftRewards: true,
    tokenRewards: 1000,
    daoAccess: true,
    defiBenefits: true
  }
]

export default function Web3SubscriptionPlans({ 
  creatorAddress, 
  onSubscribe, 
  currentPlan,
  isLoading = false 
}: Web3SubscriptionPlansProps) {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [billingCycle, setBillingCycle] = useState<'month' | 'year'>('month')

  const getYearlyDiscount = (monthlyPrice: number) => {
    return Math.round(monthlyPrice * 12 * 0.8) // 20% discount for yearly
  }

  return (
    <div className="min-h-screen relative overflow-hidden"
         style={{
           background: `
             linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
             radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
             radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
           `,
           backgroundSize: '100% 100%, 800px 800px, 900px 900px'
         }}>
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-2 h-2 bg-gold-400 rounded-full animate-pulse opacity-60"></div>
        <div className="absolute top-40 right-20 w-1 h-1 bg-purple-400 rounded-full animate-pulse opacity-40" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-1/4 w-3 h-3 bg-violet-400 rounded-full animate-pulse opacity-30" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-20 right-1/3 w-2 h-2 bg-gold-400 rounded-full animate-pulse opacity-50" style={{ animationDelay: '0.5s' }}></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 
            className="text-4xl font-bold mb-4 bg-gradient-to-r from-gold-400 via-gold-500 to-violet-400 bg-clip-text text-transparent"
            style={{
              fontFamily: 'Georgia, serif',
              textShadow: `
                1px 1px 0px rgba(0, 0, 0, 0.4),
                -1px -1px 0px rgba(0, 0, 0, 0.4),
                1px -1px 0px rgba(0, 0, 0, 0.4),
                -1px 1px 0px rgba(0, 0, 0, 0.4),
                0px 0px 4px rgba(0, 0, 0, 0.3)
              `
            }}
          >
            🚀 Web3 Subscription Plans
          </h1>
          <p className="text-purple-200 text-lg max-w-2xl mx-auto">
            Choose your level of support and unlock exclusive Web3 benefits including NFTs, token rewards, and governance participation
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-1">
            <button
              onClick={() => setBillingCycle('month')}
              className={`px-4 py-2 rounded-md transition-all duration-300 ${
                billingCycle === 'month'
                  ? 'bg-gradient-to-r from-gold-500 to-gold-600 text-black font-semibold'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('year')}
              className={`px-4 py-2 rounded-md transition-all duration-300 ${
                billingCycle === 'year'
                  ? 'bg-gradient-to-r from-gold-500 to-gold-600 text-black font-semibold'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Yearly <span className="text-xs bg-green-500 text-white px-1 rounded">-20%</span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {web3Plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                plan.popular ? 'ring-2 ring-gold-400' : ''
              } ${selectedPlan === plan.id ? 'ring-2 ring-purple-400' : ''}`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="absolute top-4 right-4">
                  <span className="bg-gradient-to-r from-gold-500 to-gold-600 text-black text-xs font-bold px-3 py-1 rounded-full">
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="p-6">
                {/* Plan Header */}
                <div className="text-center mb-6">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${plan.color} mb-4`}>
                    {plan.icon}
                  </div>
                  <h3 
                    className="text-2xl font-bold text-white mb-2"
                    style={{ fontFamily: 'Georgia, serif' }}
                  >
                    {plan.name}
                  </h3>
                  <p className="text-purple-300 text-sm mb-4">
                    {plan.description}
                  </p>
                  
                  {/* Price */}
                  <div className="mb-4">
                    <div className="text-4xl font-bold text-white">
                      {billingCycle === 'year' ? getYearlyDiscount(plan.price) : plan.price}
                      <span className="text-lg text-purple-300"> {plan.currency}</span>
                    </div>
                    <div className="text-purple-300 text-sm">
                      per {billingCycle === 'year' ? 'year' : 'month'}
                      {billingCycle === 'year' && (
                        <span className="block text-green-400 text-xs">
                          Save {plan.price * 12 - getYearlyDiscount(plan.price)} {plan.currency}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Web3 Rewards */}
                  {plan.tokenRewards && (
                    <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-center gap-2 text-yellow-400">
                        <Coins className="w-4 h-4" />
                        <span className="text-sm font-semibold">
                          {plan.tokenRewards} Creator Tokens
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  <h4 className="text-white font-semibold text-sm">Traditional Benefits:</h4>
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-purple-200 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Web3 Features */}
                <div className="space-y-3 mb-6">
                  <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                    <Globe className="w-4 h-4 text-blue-400" />
                    Web3 Benefits:
                  </h4>
                  {plan.web3Features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-4 h-4 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 mt-0.5 flex-shrink-0 flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      <span className="text-purple-200 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Special Features */}
                <div className="space-y-2 mb-6">
                  {plan.nftRewards && (
                    <div className="flex items-center gap-2 text-violet-400 text-sm">
                      <Gem className="w-4 h-4" />
                      <span>Exclusive NFT Rewards</span>
                    </div>
                  )}
                  {plan.daoAccess && (
                    <div className="flex items-center gap-2 text-blue-400 text-sm">
                      <Shield className="w-4 h-4" />
                      <span>DAO Governance Access</span>
                    </div>
                  )}
                  {plan.defiBenefits && (
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <Zap className="w-4 h-4" />
                      <span>DeFi Yield Sharing</span>
                    </div>
                  )}
                </div>

                {/* Subscribe Button */}
                <button
                  onClick={() => {
                    setSelectedPlan(plan.id)
                    onSubscribe(plan.id, billingCycle === 'year' ? getYearlyDiscount(plan.price) : plan.price)
                  }}
                  disabled={isLoading || currentPlan === plan.id}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-300 ${
                    currentPlan === plan.id
                      ? 'bg-green-500/20 border border-green-400 text-green-400'
                      : `bg-gradient-to-r ${plan.color} hover:shadow-lg hover:scale-105 text-white`
                  }`}
                >
                  {currentPlan === plan.id ? (
                    <span className="flex items-center justify-center gap-2">
                      <Check className="w-4 h-4" />
                      Current Plan
                    </span>
                  ) : isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Rocket className="w-4 h-4" />
                      Subscribe Now
                    </span>
                  )}
                </button>
              </div>

              {/* Hover Effect Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 
              className="text-2xl font-bold text-white mb-4"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              🌐 Web3 Benefits Explained
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Gem className="w-5 h-5 text-violet-400" />
                  <span className="text-white font-semibold">NFT Rewards</span>
                </div>
                <p className="text-purple-300 text-sm">
                  Exclusive digital collectibles and artwork created by the creator, 
                  with blockchain verification and ownership rights.
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Coins className="w-5 h-5 text-yellow-400" />
                  <span className="text-white font-semibold">Token Rewards</span>
                </div>
                <p className="text-purple-300 text-sm">
                  Creator tokens that can be used for governance, special privileges, 
                  and potential future value appreciation.
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-blue-400" />
                  <span className="text-white font-semibold">DAO Access</span>
                </div>
                <p className="text-purple-300 text-sm">
                  Participate in decentralized governance, vote on creator decisions, 
                  and help shape the future of the community.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
